import { format, isAfter, isValid } from 'date-fns';
import { useFormikContext } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import { Keyboard, Modal, Platform, TextInputProps, View } from 'react-native';
import { Calendar } from 'react-native-calendars';
import { TextInputMask, TextInputMaskProps } from 'react-native-masked-text';
import styled from 'styled-components/native';
import { TouchableWithoutFeedback } from 'react-native-gesture-handler';
import { Feather, Ionicons } from '@expo/vector-icons';

import Colors from '../../constants/Colors';
import { useFormContext } from './Form';
import DateTimePicker from '@react-native-community/datetimepicker';
import { B } from '../Typography';
import Spacer from '../Spacer';
import SelectField from '../form/SelectField';

const FormInputView = styled.View`
  width: 100%;
`;

const LabelText = styled.Text<{ error?: boolean }>`
  font-family: 'DMSans-Bold';
  font-size: 16px;
  line-height: 20px;
  color: ${p => (p.error ? Colors.error : Colors.secondaryText)};
  margin-bottom: 2px;
`;

const StyledCoverView = styled.View`
  position: absolute;
  width: 100%;
  height: 100%;
`;

const StyledTouchableWithoutFeedback = styled(TouchableWithoutFeedback)`
  width: 100%;
  height: 100%;
`;

const StyledSpinnerView = styled.View`
  border-radius: 18px;
  align-items: center;
  justify-content: center;
  width: 340px;
  background-color: white;
`;

const StyledDateTimePicker = styled(DateTimePicker)`
  width: 100%;
`;

const StyledButton = styled.TouchableOpacity`
  width: 100%;
  max-width: 340px;
  background-color: white;
  border-radius: 15px;
  align-items: center;
  justify-content: center;
  height: 40px;
`;

const StyledRowContainer = styled.View`
  flex-direction: row;
`;

const StyledFormSelectContainer = styled.View<{
  paddingRight?: number;
  widthPercent: number;
}>`
  padding-right: ${p => (p.paddingRight ? p.paddingRight : 0)}px;
  width: ${p => p.widthPercent}%;
  height: 62px;
`;

const StyledView = styled.View`
  flex: 1;
  align-items: center;
  justify-content: center;
`;

const StyledTextInputView = styled.View<{
  minHeight?: number;
  error?: boolean;
  hasHelperText: boolean;
}>`
  background-color: #fff;
  min-height: ${p => (p.minHeight ? `${p.minHeight}px` : 'auto')};
  border: 1px solid ${p => (p.error ? Colors.error : Colors.formInputBorder)};
  margin-bottom: ${p => (p.error || p.hasHelperText ? 4 : 12)}px;
  border-radius: 8px;
  flex-direction: row;
  height: 64px;
`;

const StyledModalView = styled.View`
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
`;

const StyledModalBackgroundContainer = styled.TouchableWithoutFeedback`
  width: 100%;
  height: 100%;
  position: absolute;
  align-items: center;
  justify-content: center;
`;

const StyledModalBackground = styled.View`
  width: 100%;
  height: 100%;
  position: absolute;
  background-color: black;
  opacity: 0.3;
`;

const StyledTextInputMask = styled(TextInputMask)<{ empty?: boolean }>`
  font-family: ${p => (p.empty ? 'WorkSans-Regular' : 'DMSans-Bold')};
  font-size: 18px;
  padding: 16px;
  flex: 1;
` as React.FC<
  TextInputMaskProps & {
    ref: any;
    empty?: boolean;
  }
>;

const StyledCalendarView = styled.View`
  background-color: white;
  width: 100%;
  padding-top: 10px;
  padding-bottom: 10px;
  max-width: 350px;
  border-radius: 8px;
`;
const StyledCalendarIconView = styled.TouchableOpacity`
  align-self: center;
  margin-right: 8px;
`;

const HelperText = styled.Text`
  font-family: 'DMSans-Medium';
  font-size: 12px;
  line-height: 18px;
  color: ${Colors.formLabel};
  margin: 4px 4px 12px 4px;
`;

const ErrorText = styled(HelperText)`
  color: ${Colors.error};
`;

export type FormInputProps = TextInputProps & {
  name: string;
  label?: string;
  helperText?: string;
  showPlaceHolder?: boolean;
  birthDate?: boolean;
  autoSubmit?: boolean;
};

const FormDateInput: React.FC<FormInputProps> = ({
  name,
  label,
  helperText,
  showPlaceHolder,
  birthDate,
  autoSubmit,
  ...props
}) => {
  const textInputMaskRef = useRef<TextInputMask>(null!);
  const {
    handleChange,
    handleBlur,
    touched,
    errors,
    values,
    setFieldError,
    setErrors,
    dirty,
    isValid: isValidValues,
    handleSubmit
  } = useFormikContext<any>();
  const { setFocusedInput, focusNext } = useFormContext();
  const [height, setHeight] = useState<number>();
  const error = dirty && errors[name];
  const [showCalendar, setShowCalendar] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  const [selectedBirthDateIOS, setSelectedBirthDateIOS] = useState<number>();

  const getDate = () => {
    const date = new Date();
    date.setDate(date.getDate());
    const dateKey = format(date, 'yyyy-MM-dd');
    return dateKey;
  };

  const todayMarkStyle = {
    selected: true,
    selectedColor: '#8c538a',
    selectedTextColor: Colors.lightText
  };

  const selectedMarkStyle = {
    selected: true,
    selectedColor: Colors.primaryPurple,
    selectedTextColor: 'white'
  };

  const [markedDates, setMarkedDates] = useState({
    [getDate()]: todayMarkStyle
  });

  const toDateString = (date: string) => {
    const dateArr = date.split('/');
    if (dateArr.length === 3) {
      if (dateArr[2].length === 4) {
        return dateArr[2] + '-' + dateArr[0] + '-' + dateArr[1];
      }
    }
    return getDate();
  };

  const toDateInput = (date: string) => {
    const dateArr = date.split('-');
    if (dateArr.length === 3) {
      return dateArr[1] + '/' + dateArr[2] + '/' + dateArr[0];
    } else {
      return '';
    }
  };

  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ];

  const [webBirthDate, setWebBirthDate] = useState<{
    month: string;
    year: string;
    day: string;
  }>(
    values[name]
      ? {
          month: monthNames[Number(String(values[name]).split('/')[0]) - 1],
          year: String(values[name]).split('/')[2],
          day: String(values[name]).split('/')[1]
        }
      : {
          month: '',
          year: '',
          day: ''
        }
  );

  const getYears = () => {
    const res: string[] = [];
    const latestYear = new Date().getFullYear();
    for (let i = 0; i < 150; i++) {
      res.push(String(latestYear - i));
    }
    return res;
  };

  const daysInMonth = (month: number, year: number, def?: boolean) => {
    const res: string[] = [];
    if (def) {
      for (let i = 0; i < 31; i++) {
        if (String(i + 1).length === 1) {
          res.push('0' + String(i + 1));
        } else {
          res.push(String(i + 1));
        }
      }
    } else {
      for (let i = 0; i < new Date(year, month + 1, 0).getDate(); i++) {
        if (String(i + 1).length === 1) {
          res.push('0' + String(i + 1));
        } else {
          res.push(String(i + 1));
        }
      }
    }

    return res;
  };

  // sets the day to the number of days of the specific month and year if it exeeded it
  useEffect(() => {
    if (
      Platform.OS === 'web' &&
      birthDate &&
      webBirthDate.month &&
      webBirthDate.year &&
      webBirthDate.day
    ) {
      if (
        Number(webBirthDate.day) >
        daysInMonth(
          monthNames.indexOf(webBirthDate.month),
          Number(webBirthDate.year)
        ).length
      ) {
        setWebBirthDate({
          month: webBirthDate.month,
          year: webBirthDate.year,
          day: String(
            daysInMonth(
              monthNames.indexOf(webBirthDate.month),
              Number(webBirthDate.year)
            ).length
          )
        });
      }
      const date = new Date();
      date.setFullYear(Number(webBirthDate.year));
      date.setMonth(monthNames.indexOf(webBirthDate.month));
      date.setDate(Number(webBirthDate.day));
      if (isValid(date)) {
        handleChange(name)(format(date, 'MM/dd/yyyy'));
      }
    }
  }, [webBirthDate]);

  useEffect(() => {
    if (autoSubmit && dirty && isValidValues) {
      setTimeout(() => {
        handleSubmit();
      }, 100);
    }
  }, [autoSubmit, dirty, handleSubmit, isValidValues]);

  return (
    <FormInputView>
      {label && <LabelText error={Boolean(error)}>{label}</LabelText>}
      {birthDate && Platform.OS === 'web' ? (
        <StyledRowContainer>
          <StyledFormSelectContainer widthPercent={50} paddingRight={8}>
            <SelectField
              placeholder='Month'
              value={webBirthDate.month || ' '}
              options={monthNames}
              onChange={(option: any) => {
                setWebBirthDate({
                  month: String(option.value),
                  year: webBirthDate.year,
                  day: webBirthDate.day
                });
              }}
            />
          </StyledFormSelectContainer>
          <StyledFormSelectContainer widthPercent={25} paddingRight={8}>
            <SelectField
              placeholder='Day'
              value={webBirthDate.day || ' '}
              options={
                webBirthDate.month && webBirthDate.year
                  ? daysInMonth(
                      monthNames.indexOf(webBirthDate.month),
                      Number(webBirthDate.year)
                    )
                  : daysInMonth(0, 0, true)
              }
              onChange={(option: any) => {
                setWebBirthDate({
                  month: webBirthDate.month,
                  year: webBirthDate.year,
                  day: String(option.value)
                });
              }}
            />
          </StyledFormSelectContainer>
          <StyledFormSelectContainer widthPercent={25}>
            <SelectField
              placeholder='Year'
              value={webBirthDate.year || ' '}
              options={getYears()}
              onChange={(option: any) => {
                setWebBirthDate({
                  month: webBirthDate.month,
                  year: String(option.value),
                  day: webBirthDate.day
                });
              }}
            />
          </StyledFormSelectContainer>
        </StyledRowContainer>
      ) : (
        <StyledTextInputView
          minHeight={height}
          hasHelperText={Boolean(helperText)}
          error={Boolean(error)}
        >
          <StyledTextInputMask
            type={'datetime'}
            options={{
              format: 'MM/DD/YYYY',
              mask: 'MM/DD/YYYY'
            }}
            blurOnSubmit={false}
            ref={(textInput: TextInputMask) => {
              textInputMaskRef.current = textInput;
            }}
            {...props}
            value={values[name]}
            empty={!values[name]}
            onChangeText={text => {
              handleChange(name)(text);
              setMarkedDates({
                [toDateString(text)]: selectedMarkStyle
              });
            }}
            onBlur={event => {
              handleBlur(name)(event);
              setFocusedInput(undefined);
            }}
            onContentSizeChange={event => {
              setHeight(event.nativeEvent.contentSize.height);
            }}
            onSubmitEditing={() => {
              focusNext();
              Keyboard.dismiss();
            }}
            placeholder={showPlaceHolder ? 'MM/DD/YYYY' : ''}
            placeholderTextColor={Colors.placeholderColor}
          />
          {!birthDate && (
            <StyledCalendarIconView
              onPress={() => {
                setShowCalendar(true);
              }}
            >
              <Ionicons
                name='calendar'
                size={24}
                color={Colors.placeholderColor}
              />
            </StyledCalendarIconView>
          )}
          {birthDate && Platform.OS !== 'web' && (
            <StyledCoverView>
              <StyledTouchableWithoutFeedback
                onPress={() => {
                  setShowSpinner(true);
                }}
              >
                <View />
              </StyledTouchableWithoutFeedback>
            </StyledCoverView>
          )}
        </StyledTextInputView>
      )}

      {error ? (
        <ErrorText>{error}</ErrorText>
      ) : (
        helperText && <HelperText>{helperText}</HelperText>
      )}
      {birthDate && Platform.OS === 'android' && showSpinner && (
        <DateTimePicker
          mode='date'
          value={
            values[name] ? new Date(toDateString(values[name])) : new Date()
          }
          display='spinner'
          onChange={date => {
            setShowSpinner(false);
            if (
              typeof date.nativeEvent.timestamp !== 'undefined' &&
              date.type === 'set'
            ) {
              handleChange(name)(
                format(new Date(date.nativeEvent.timestamp), 'MM/dd/yyyy')
              );
            }
          }}
        />
      )}
      {birthDate && Platform.OS === 'ios' && (
        <Modal visible={showSpinner} transparent={true} animationType='fade'>
          <StyledModalBackgroundContainer
            onPress={() => {
              setShowSpinner(false);
            }}
          >
            <StyledView>
              <StyledModalBackground />
              <StyledSpinnerView>
                <StyledDateTimePicker
                  onChange={date => {
                    setSelectedBirthDateIOS(date.nativeEvent.timestamp);
                  }}
                  mode='date'
                  value={
                    typeof selectedBirthDateIOS !== 'undefined'
                      ? new Date(selectedBirthDateIOS)
                      : values[name]
                      ? new Date(toDateString(values[name]))
                      : new Date()
                  }
                  display='spinner'
                />
              </StyledSpinnerView>
              <Spacer size={1} />
              <StyledButton
                onPress={() => {
                  setShowSpinner(false);
                }}
              >
                <B color='dark'>Cancel</B>
              </StyledButton>
              <Spacer size={1} />
              <StyledButton
                onPress={() => {
                  setShowSpinner(false);
                  handleChange(name)(
                    format(
                      typeof selectedBirthDateIOS !== 'undefined'
                        ? new Date(selectedBirthDateIOS)
                        : new Date(),
                      'MM/dd/yyyy'
                    )
                  );
                }}
              >
                <B>Save</B>
              </StyledButton>
            </StyledView>
          </StyledModalBackgroundContainer>
        </Modal>
      )}
      <Modal visible={showCalendar} transparent={true} animationType='fade'>
        <StyledModalView>
          <StyledModalBackgroundContainer
            onPress={() => {
              setShowCalendar(false);
            }}
          >
            <StyledModalBackground />
          </StyledModalBackgroundContainer>
          <StyledCalendarView>
            <Calendar
              current={Object.keys(markedDates)[0]}
              markedDates={{
                [getDate()]: todayMarkStyle,
                ...markedDates
              }}
              enableSwipeMonths={true}
              hideExtraDays={true}
              onDayPress={(day: any) => {
                handleChange(name)(toDateInput(day.dateString));
                setMarkedDates({
                  [day.dateString]: selectedMarkStyle
                });
                setShowCalendar(false);
              }}
              renderArrow={(direction: any) => (
                <Feather
                  name={direction === 'left' ? 'chevron-left' : 'chevron-right'}
                  size={28}
                />
              )}
              theme={{
                textDayFontFamily: 'DMSans-Bold',
                textMonthFontFamily: 'DMSans-Bold',
                textDayHeaderFontFamily: 'DMSans-Bold',
                textDayFontSize: 12,
                dayTextColor: Colors.primary,
                monthTextColor: Colors.primary,
                textMonthFontSize: 16
              }}
            />
          </StyledCalendarView>
        </StyledModalView>
      </Modal>
    </FormInputView>
  );
};

export default FormDateInput;
